import React, { createContext, useContext, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

import Dashboard from "@/pages/Dashboard";
import SettingsPage from "@/pages/SettingsPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = false;

// Admin (padlock) token. Only used to gate Settings mutations — the
// dashboard itself is fully public. Stored in sessionStorage so it dies
// when the tab closes (you re-enter the padlock once per session).
const ADMIN_KEY = "ems_admin_token";
export const getAdminToken = () => {
  try { return sessionStorage.getItem(ADMIN_KEY) || null; } catch { return null; }
};
export const setAdminToken = (tok) => {
  try {
    if (tok) sessionStorage.setItem(ADMIN_KEY, tok);
    else sessionStorage.removeItem(ADMIN_KEY);
  } catch { /* ignore */ }
};

// Build an axios config that includes the admin Bearer header (used for
// PUT /settings and POST /settings/test-connection only).
export const adminAuth = () => {
  const tok = getAdminToken();
  return tok ? { headers: { Authorization: `Bearer ${tok}` } } : {};
};

const AdminContext = createContext(null);
export const useAdmin = () => useContext(AdminContext);

function AdminProvider({ children }) {
  const [unlocked, setUnlocked] = useState(!!getAdminToken());
  const unlock = (token) => {
    setAdminToken(token);
    setUnlocked(true);
  };
  const lock = () => {
    setAdminToken(null);
    setUnlocked(false);
  };
  return (
    <AdminContext.Provider value={{ unlocked, unlock, lock }}>
      {children}
    </AdminContext.Provider>
  );
}

function AppRouter() {
  return (
    <Routes>
      <Route path="/dashboard" element={<Dashboard />} />
      <Route path="/settings" element={<SettingsPage />} />
      {/* Old routes redirect to dashboard — login removed in v3.1 */}
      <Route path="/login" element={<Navigate to="/dashboard" replace />} />
      <Route path="/meters" element={<Navigate to="/settings" replace />} />
      <Route path="/" element={<Navigate to="/dashboard" replace />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AdminProvider>
          <AppRouter />
          <Toaster position="top-right" theme="dark" />
        </AdminProvider>
      </BrowserRouter>
    </div>
  );
}
